/**
 * Lane FRONT-FIGURES: the front stage's figures on the same grids as in play (density option C, render/density.ts).
 *
 * - The stage canvas is backed at `R` canvas pixels per art pixel and drawn with a ×R transform, so the scenes keep
 *   painting on the art grid (G1) unchanged while a figure's 2× frame lands 1:1 on the backing: the figure grid
 *   (G2, half an art pixel), as the game draws it. Before, the 2× frames were squeezed onto the art-pixel canvas by
 *   nearest-neighbour, dropping every other row and column (thin outlines broke, faces blotched, the figure read
 *   coarser than the scene).
 * - Where G2 can't be shown (a window at one CSS pixel per art pixel, the low tiers' 1× frames, a figure painted
 *   into a scene's art-res layer, or a scene listed in `COARSE_SCENES`), the frame is brought to the art grid the way
 *   the 1× tiers' frames are (`toArtRes`: majority colour, outline mended), once per frame image.
 * - Feet meet the ground: a pixel contact shadow (render/contactShadow.ts, as under figures in play) on G1 under each
 *   standing figure.
 */
import { densityC } from '../render/density';
import { resOf, toArtRes } from '../render/pixel';

/**
 * Scenes kept on the art grid for now: their water reflection (scenes/lofiKit.ts `reflect`) copies the stage canvas
 * by art-pixel rects, which on a 2× backing would mirror the wrong band. Remove an id here once `reflect` scales its
 * source rect by `s.main.canvas.width / s.LW`.
 */
export const COARSE_SCENES: ReadonlySet<string> = new Set([]);

/** Backing pixels per art pixel for a window scale `S` (CSS px per art px) and a scene. */
export function stageRes(S: number, scene: string | null): number {
  return densityC() && S >= 2 && !(scene && COARSE_SCENES.has(scene)) ? 2 : 1;
}

const ART = new WeakMap<HTMLCanvasElement, HTMLCanvasElement>();
/** A figure frame on the art grid (cached per frame image; frames already at 1× are returned as they are). */
export function onArtGrid(img: HTMLCanvasElement): HTMLCanvasElement {
  if (resOf(img) !== 2) return img;
  let a = ART.get(img);
  if (!a) ART.set(img, (a = toArtRes(img)));
  return a;
}

/** The contact shadow under a standing figure, art px: half-width, half-height, opacity. */
export const CONTACT = { rx: 6, ry: 2, alpha: 0.42 } as const;
